import { useEffect, useRef, useState } from "react";
import type { DeleteResult } from "../hooks/useClaws";
import type { OpenClaw } from "../types/openclaw";
import styles from "./DeleteModal.module.css";

type Props = {
  open: boolean;
  claw: OpenClaw | null;
  onClose: () => void;
  onSubmit: (tenantId: string) => Promise<DeleteResult>;
};

export const DeleteModal = ({ open, claw, onClose, onSubmit }: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [typed, setTyped] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setTyped("");
      setServerError(null);
      setSubmitting(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  if (!open || !claw) return null;

  const matches = typed === claw.name;

  const handleSubmit = async (evt: React.FormEvent): Promise<void> => {
    evt.preventDefault();
    if (!matches) return;
    setSubmitting(true);
    setServerError(null);
    const result = await onSubmit(claw.tenantId);
    setSubmitting(false);
    if (result.ok) {
      onClose();
      return;
    }
    setServerError(result.message);
  };

  const disabled = submitting || !matches;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting) onClose();
      }}
    >
      <form method="dialog" className={styles.form} onSubmit={handleSubmit}>
        <header className={styles.header}>
          <h2 className={styles.title}>Delete OpenClaw</h2>
          <p className={styles.subtitle}>
            This permanently terminates the EC2 instance and removes all tenant records.
            This cannot be undone.
          </p>
        </header>

        <label className={styles.field}>
          <span className={styles.label}>
            Type <span className={styles.nameToken}>{claw.name}</span> to confirm
          </span>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              setServerError(null);
            }}
            autoComplete="off"
            disabled={submitting}
            aria-invalid={typed.length > 0 && !matches}
          />
        </label>

        {serverError && <div className={styles.errorBanner}>{serverError}</div>}

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className={styles.dangerBtn} disabled={disabled}>
            {submitting ? "Deleting…" : "Delete instance"}
          </button>
        </footer>
      </form>
    </dialog>
  );
};
